import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, DatePicker, Checkbox, Spin, Popconfirm } from "antd";
import {
  fetchExperiences,
  addExperience,
  updateExperience,
  deleteExperience,
  handleNextStep,
  handlePrevStep,
} from "../../../features/profile/profileSlice";
import { showToastError, showToastSuccess } from "../../../utils";
import toast from "react-hot-toast";
import expImg from "../../../assets/eduImg.png";
import dayjs from "dayjs";

const { TextArea } = Input;

function closeModal() {
  toast.dismiss();
}

export default function ExperienceStep() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const { profileLoading, experiences = [] } = useSelector(
    (state) => state.profile
  );

  const [showForm, setShowForm] = useState(false);
  const [isCurrent, setIsCurrent] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    dispatch(fetchExperiences());
  }, [dispatch]);

  const onFinish = async (values) => {
    const payload = {
      employerName: values.employerName,
      jobTitle: values.jobTitle,
      startDate: values.startDate
        ? dayjs(values.startDate).format("YYYY-MM-DD")
        : null,
      endDate:
        isCurrent || !values.endDate
          ? null
          : dayjs(values.endDate).format("YYYY-MM-DD"),
      isCurrentJob: isCurrent,
      workDescription: values.workDescription || "",
    };

    const res = editingId
      ? await dispatch(updateExperience({ id: editingId, data: payload }))
      : await dispatch(addExperience(payload));

    if (res?.payload?.success) {
      showToastSuccess(
        editingId ? "Experience updated successfully" : "Experience added successfully",
        closeModal
      );
      form.resetFields();
      setShowForm(false);
      setEditingId(null);
      setIsCurrent(false);
      dispatch(fetchExperiences());
    } else {
      showToastError(
        res?.payload?.message || "Failed to save experience",
        closeModal
      );
    }
  };

  function handleEdit(item) {
    setEditingId(item.id);
    setShowForm(true);
    setIsCurrent(!!item.isCurrentJob);
    form.setFieldsValue({
      employerName: item.employerName,
      jobTitle: item.jobTitle,
      startDate: item.startDate ? dayjs(item.startDate) : null,
      endDate: item.endDate ? dayjs(item.endDate) : null,
      workDescription: item.workDescription || "",
    });
  }

  async function handleDelete(id) {
    const res = await dispatch(deleteExperience(id));
    if (res?.payload?.success) {
      showToastSuccess("Experience deleted successfully", closeModal);
      dispatch(fetchExperiences());
    } else {
      showToastError(
        res?.payload?.message || "Failed to delete experience",
        closeModal
      );
    }
  }

  function handleGoNext() {
    dispatch(handleNextStep());
  }

  function handleGoBack() {
    dispatch(handlePrevStep());
  }

  function handleCancel() {
    form.resetFields();
    setIsCurrent(false);
    setShowForm(false);
    setEditingId(null);
  }

  if (profileLoading && !experiences.length && !showForm) {
    return (
      <div className="flex items-center justify-center mt-20">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="mt-10">
      <h3 className="edit-step-head">Experience</h3>
      <p className="edit-step-desc mt-2">
        Tell us about your work experience so far. Feel free to include
        volunteer work and internships too.
      </p>

      <div className="flex justify-end w-full">
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-transparent flex items-center border-none"
          >
            <div className="bg-darkBlue rounded-full p-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4 16C4 15.4477 4.44772 15 5 15H27C27.5523 15 28 15.4477 28 16C28 16.5523 27.5523 17 27 17H5C4.44772 17 4 16.5523 4 16Z"
                  fill="white"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16 4C16.5523 4 17 4.44772 17 5V27C17 27.5523 16.5523 28 16 28C15.4477 28 15 27.5523 15 27V5C15 4.44772 15.4477 4 16 4Z"
                  fill="white"
                />
              </svg>
            </div>
            <span className="text-darkBlue font-medium text-[17px] ml-3">
              Add Experience
            </span>
          </button>
        )}
      </div>

      {/* Existing experiences list */}
      {experiences.length > 0 && !showForm && (
        <>
          {experiences.map((item) => (
            <div key={item.id} className="bg-white p-6 mb-4 mt-4 rounded-lg">
              <div className="w-full flex items-start justify-between gap-4">
                <div className="flex flex-col flex-1">
                  <h3 className="text-darkBlue text-[21px] font-bold">
                    {item.employerName}
                  </h3>
                  <p className="text-black333 font-medium text-[18px] my-1">
                    {item.jobTitle}
                  </p>
                  <span className="text-black333 font-normal text-[17px]">
                    {dayjs(item.startDate).format("MMM YYYY")} -{" "}
                    {item.isCurrentJob
                      ? "Present"
                      : dayjs(item.endDate).format("MMM YYYY")}
                  </span>
                  {item.workDescription && (
                    <p className="text-black2 font-normal text-[16px] text-justify mt-3">
                      {item.workDescription}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    className="text-darkBlue border border-darkBlue rounded-full px-4 py-1 text-sm font-medium hover:bg-blue-50"
                  >
                    Edit
                  </button>
                  <Popconfirm
                    title="Delete this experience?"
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(item.id)}
                  >
                    <button
                      type="button"
                      className="text-red-500 border border-red-500 rounded-full px-4 py-1 text-sm font-medium hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </Popconfirm>
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row items-center justify-center mt-10 mb-14 w-full">
            <button
              onClick={handleGoBack}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Back
            </button>
            <button
              onClick={handleGoNext}
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="button"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Empty state */}
      {!experiences.length && !showForm && (
        <>
          <div className="flex items-center justify-center flex-col">
            <img
              className="object-contain w-full max-w-[550px] h-auto"
              src={expImg}
              alt="experience"
            />
            <p className="edit-step-desc my-10">
              Don't have work experience? You may skip this part
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center mt-10 mb-14 w-full">
            <button
              onClick={handleGoBack}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Back
            </button>
            <button
              onClick={handleGoNext}
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="button"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Add/Edit experience form */}
      {showForm && (
        <Form
          layout="vertical"
          ref={formRef}
          name="add-experience"
          onFinish={onFinish}
          style={{ maxWidth: "100%", marginTop: 20 }}
          form={form}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Form.Item
              name="employerName"
              label="Employer Name"
              rules={[
                { required: true, message: "Employer name is required" },
              ]}
            >
              <Input
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. Smart People Africa"
              />
            </Form.Item>

            <Form.Item
              name="jobTitle"
              label="Job Title"
              rules={[{ required: true, message: "Job title is required" }]}
            >
              <Input
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. Software Engineer"
              />
            </Form.Item>

            <Form.Item
              name="startDate"
              label="Start Date"
              rules={[{ required: true, message: "Start date is required" }]}
            >
              <DatePicker
                style={{ width: "100%", height: "44px" }}
                format="YYYY-MM-DD"
                placeholder="Select start date"
                picker="month"
              />
            </Form.Item>

            <div className="flex flex-col">
              <Form.Item
                name="endDate"
                label="End Date"
                rules={[
                  {
                    required: !isCurrent,
                    message: "End date is required",
                  },
                ]}
              >
                <DatePicker
                  disabled={isCurrent}
                  style={{ width: "100%", height: "44px" }}
                  format="YYYY-MM-DD"
                  placeholder="Select end date"
                  picker="month"
                />
              </Form.Item>
              <Checkbox
                checked={isCurrent}
                onChange={(e) => setIsCurrent(e.target.checked)}
              >
                <span className="check-text">This is my current job</span>
              </Checkbox>
            </div>
          </div>

          <div className="mt-4">
            <p className="mb-1 font-medium text-[15px]">Work Description</p>
            <p className="label-info">
              Tell future employers what kind of work you did, so they can get a
              good understanding of your experience.
            </p>
            <div className="w-full mt-3">
              <Form.Item name="workDescription" className="w-full">
                <TextArea rows={6} placeholder="Describe your work..." />
              </Form.Item>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-end mt-10 mb-14 w-full">
            <button
              onClick={handleCancel}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Cancel
            </button>
            <button
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="submit"
              disabled={profileLoading}
            >
              {profileLoading ? <Spin size="small" /> : editingId ? "Update" : "Save"}
            </button>
          </div>
        </Form>
      )}
    </div>
  );
}
